import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useSite } from "@/lib/useSite";
import { PublicLayout, PageIntro, FinalCta, Reveal, ImageReveal } from "@/components/site";
import { ProjectGalleryCarousel } from "@/components/ProjectGalleryCarousel";
import { ProjectCard } from "@/components/home/HomeWork";
import { BOOKING_URL } from "@/lib/booking";
import { BrandLogo } from "@/components/BrandLogo";

export default function Work() {
  const { projects, settings, loaded } = useSite();
  const portfolioProjects = projects.filter((project) => project.portfolio_visible !== false);
  return (
    <PublicLayout>
      <PageIntro label="Work" title="Selected projects." text="A small selection of recent listings across our markets." />
      <section data-testid="work-grid" className="bg-[#0B0C0E] px-6 py-20 sm:px-10 md:py-28 lg:px-16"><div className="mx-auto grid max-w-[1500px] grid-cols-1 gap-x-6 gap-y-16 md:grid-cols-12 md:gap-y-24">
        {portfolioProjects.map((p, index) => {
          const rightColumn = index % 2 === 1;
          return <ProjectCard key={p.id} p={p} editorial aspect={rightColumn ? "aspect-[4/5]" : "aspect-[16/10]"} className={`${rightColumn ? "md:col-span-5 md:col-start-8 md:pt-24" : "md:col-span-7"}`} />;
        })}
        {!loaded && <p data-testid="work-loading-state" className="col-span-12 text-zinc-500">Loading selected projects.</p>}
        {loaded && portfolioProjects.length === 0 && <p data-testid="work-empty-state" className="col-span-12 text-zinc-500">Projects coming soon.</p>}
      </div>
      </section>
      <FinalCta image={settings.cta_image_url} />
    </PublicLayout>
  );
}

export function ProjectDetail() {
  const { slug } = useParams();
  const { projects } = useSite();
  const [p, setP] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setP(null); setMissing(false);
    api.get(`/public/projects/${slug}`).then((r) => setP(r.data)).catch(() => setMissing(true));
  }, [slug]);

  const portfolioProjects = projects.filter((project) => project.portfolio_visible !== false);
  const idx = portfolioProjects.findIndex((x) => x.slug === slug);
  const next = portfolioProjects.length ? portfolioProjects[(idx + 1) % portfolioProjects.length] : null;

  if (missing) return <PublicLayout><PageIntro label="Work" title="Project not found." /></PublicLayout>;
  if (!p) return <PublicLayout><div className="h-screen" /></PublicLayout>;

  const gallery = [...new Set((p.images || []).filter(Boolean))];
  return (
    <PublicLayout>
      <section className="relative pt-20 bg-[#0B0C0E]">
        <ImageReveal src={p.cover_image} alt={p.title} className="aspect-[16/9] md:h-[85vh] md:aspect-auto" imgClassName="scale-[1.02]" />
        <div data-testid="project-cover-mandalay-mark" className="absolute top-28 left-6 z-10 h-12 w-40 pointer-events-none drop-shadow-[0_2px_5px_rgba(0,0,0,0.65)] sm:left-10 sm:h-14 sm:w-44 lg:left-16"><BrandLogo location="public-project-cover-watermark" /></div>
      </section>
      <section data-testid="project-detail" className="px-6 sm:px-10 lg:px-16 py-20 md:py-32 grid lg:grid-cols-12 gap-10 bg-[#0B0C0E]">
        <Reveal className="lg:col-span-7">
          <div className="eyebrow">{p.services}</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl mt-6 leading-[.9] text-white">{p.title}</h1>
        </Reveal>
        <Reveal className="lg:col-span-5 lg:pt-16" delay={0.1}>
          <p data-testid="project-description" className="text-zinc-400 leading-relaxed">{p.description}</p>
          <a href={BOOKING_URL} data-testid="project-book-btn" className="btn-dark mt-10">Book a Shoot</a>
        </Reveal>
      </section>
      {p.video_url && (
        <section className="px-6 sm:px-10 lg:px-16 pb-24 bg-[#0B0C0E]">
          <video data-testid="project-video" src={p.video_url} controls playsInline className="w-full aspect-video bg-slate-900" />
        </section>
      )}
      {gallery.length > 0 && <ProjectGalleryCarousel projectTitle={p.title} images={gallery} />}
      {next && next.slug !== slug && (
        <section className="px-6 sm:px-10 lg:px-16 pb-24 border-t border-white/10 pt-16 bg-[#121418]">
          <div className="eyebrow mb-8">Next project</div>
          <ProjectCard p={next} testid="project-next" />
        </section>
      )}
    </PublicLayout>
  );
}
